import { readableAccent } from "@eldrun/content";
import { h } from "../kit/dom.ts";
import type { PartyMember } from "./party-model.ts";
import { renderStatusChips } from "./status-glyphs.ts";

interface Row {
  root: HTMLElement;
  badge: HTMLElement;
  name: HTMLElement;
  hp: HTMLElement;
  patua: HTMLElement;
  info: HTMLElement;
  klass: HTMLElement;
  statuses: HTMLElement;
}

/**
 * Quadros do grupo (F63, doc 09 §5.2): na borda esquerda, um por aliado, com o hexágono numerado na cor
 * do slot, o nome, Vida + Égide e o estado (caído, desconectado, distância). Linhas mantidas por id e
 * atualizadas no lugar; só mexe no DOM quando a projeção muda.
 */
export class PartyFrames {
  readonly element: HTMLElement;
  private readonly rows = new Map<string, Row>();
  private key = "";

  constructor(parent: HTMLElement) {
    this.element = h("div", { id: "grupo", "aria-label": "Grupo" });
    parent.appendChild(this.element);
  }

  update(members: readonly PartyMember[]): void {
    const key = JSON.stringify(members);
    if (key === this.key) return;
    this.key = key;
    const seen = new Set<string>();
    for (const m of members) {
      seen.add(m.id);
      const row = this.row(m.id);
      row.root.style.setProperty("--cor", m.color);
      // O número no hexágono é texto: o preenchimento é a variante legível da cor (F85, o azul clareia).
      row.root.style.setProperty("--cor-claro", readableAccent(m.color));
      row.root.dataset["slot"] = String(m.n);
      row.badge.textContent = String(m.n);
      row.name.textContent = m.name;
      row.klass.textContent = m.classGlyph;
      row.klass.title = m.className;
      row.root.setAttribute("aria-label", `${m.name}, ${m.className}, jogador ${m.n}`);
      row.statuses.style.setProperty("--fonte", m.statusColor);
      renderStatusChips(row.statuses, m.statuses);
      row.hp.style.width = `${(m.hp * 100).toFixed(1)}%`;
      row.patua.style.width = `${(m.patua * 100).toFixed(1)}%`;
      row.root.classList.toggle("caido", m.dead || m.fallen !== undefined);
      row.root.classList.toggle("ult-pronta", m.ultReady);
      row.root.classList.toggle("fora", !m.connected);
      row.info.textContent = !m.connected ? "desconectado" : m.dead ? "Lápide" : m.fallen !== undefined ? `caído · ${m.fallen} s` : m.distance !== undefined ? `${m.distance} m` : "";
    }
    for (const [id, row] of this.rows) if (!seen.has(id)) row.root.hidden = true;
    for (const id of seen) this.rows.get(id)!.root.hidden = false;
    // Ordem do slot.
    const ordered = members.map((m) => this.rows.get(m.id)!.root);
    if (ordered.some((el, i) => this.element.children[i] !== el)) this.element.append(...ordered);
  }

  private row(id: string): Row {
    let row = this.rows.get(id);
    if (!row) {
      const badge = h("span", { class: "grupo-selo", "aria-hidden": "true" });
      const name = h("span", { class: "nome" });
      const hp = h("span", { class: "vida" });
      const patua = h("span", { class: "patua" });
      const info = h("span", { class: "info" });
      const klass = h("span", { class: "classe", "aria-hidden": "true" });
      const statuses = h("span", { class: "status", "aria-label": "Status" });
      const root = h("div", { class: "membro", "data-id": id }, badge, h("span", { class: "corpo" }, h("span", { class: "linha" }, klass, name, statuses), h("span", { class: "barra" }, hp, patua), info));
      row = { root, badge, name, hp, patua, info, klass, statuses };
      this.rows.set(id, row);
      this.element.appendChild(root);
    }
    return row;
  }
}
